import {
  Field,
  FieldHelperTextProps,
  FieldRootProps,
  Input,
  InputGroup,
  InputGroupProps,
  InputProps,
  Textarea,
} from '@chakra-ui/react'
import { ChangeEvent, FocusEvent, ReactElement, ReactNode, forwardRef, useState } from 'react'
import { EyeIcon, EyeSlashIcon, IconButton, InputContainerProps } from '.'
import InputContainer from './InputContainer'

export interface Props extends Omit<InputProps, 'title' | 'variant' | 'onValueChange'> {
  controlProps?: FieldRootProps
  helperText?: string
  helperTextProps?: Omit<FieldHelperTextProps, 'children'>
  inputContainerProps?: Omit<InputContainerProps, 'label' | 'children'>
  inputGroupProps?: Omit<InputGroupProps, 'children'>
  isArea?: boolean
  isError?: boolean
  label?: ReactNode
  leftElement?: ReactElement
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void
  rightElement?: ReactElement
  variant?: 'outline' | 'subtle' | 'flushed'
}

const TextField = forwardRef<HTMLInputElement, Props>((props, ref) => {
  const [emptyError, setEmptyError] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const handleToggle = () => setShowPassword(!showPassword)

  const {
    borderRadius = 'sm',
    controlProps,
    helperText,
    helperTextProps,
    inputContainerProps,
    inputGroupProps,
    isArea = false,
    isError,
    label,
    leftElement,
    onBlur,
    onChange,
    rightElement,
    type,
    variant = 'outline',
    width,
    ...rest
  } = props

  const _isError = isError || emptyError

  const sharedInputProps = {
    ref,
    variant,
    onBlur: (e: FocusEvent<HTMLInputElement>) => {
      setEmptyError(Boolean(rest.required && !e.target.value))
      onBlur?.(e)
    },
    onChange: (e: ChangeEvent<HTMLInputElement>) => {
      if (emptyError) setEmptyError(false)
      onChange?.(e)
    },
    backgroundColor: variant == 'subtle' ? 'transparent' : 'var(--chakra-colors-bg-panel)',
    borderColor: _isError ? 'danger.500' : undefined,
    _hover: {},
  }

  return (
    <Field.Root alignItems="stretch" width={width} {...controlProps}>
      <InputContainer
        label={label}
        isError={_isError}
        required={rest.required}
        {...inputContainerProps}
      >
        <InputGroup startElement={leftElement} endElement={rightElement} {...inputGroupProps}>
          {isArea ? (
            <Textarea autoresize maxH="6lh" {...sharedInputProps} {...(rest as any)} />
          ) : type === 'password' ? (
            <InputGroup
              endElement={
                <IconButton
                  tabIndex={-1}
                  variant="ghost"
                  size="sm"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  icon={showPassword ? <EyeIcon /> : <EyeSlashIcon />}
                  onClick={handleToggle}
                />
              }
            >
              <Input {...sharedInputProps} {...rest} type={showPassword ? 'text' : 'password'} />
            </InputGroup>
          ) : (
            <Input {...sharedInputProps} {...rest} />
          )}
        </InputGroup>
      </InputContainer>

      {helperText != null && (
        <Field.HelperText
          textStyle={'bodyXSmall'}
          mt={1}
          position="absolute"
          top="100%"
          opacity={0.6}
          color={_isError ? 'danger.500' : undefined}
          {...helperTextProps}
        >
          {helperText}&nbsp;
        </Field.HelperText>
      )}
    </Field.Root>
  )
})

export default TextField
